import { useMemo } from 'react';

import { decodeUser, LoggedUser } from './session/session-decode';
import { isIntegrator, isIntegratorOrDistributor } from './session/user-type';
import { getTenantStorage } from './session/tenant-storage';
import { useLoggedPerson } from './navbar/effects/data';
import { useConfiguracoes } from './sidebar/effects/data';
import { PostLoginContext, resolvePostLoginRedirect } from './session/post-login-redirect';

const USER_INSTANCE_KEY = 'user_instance';

// Deep link de integrador guardado pelo fluxo legado; consumido uma única vez.
const PATH_INTEGRADOR_KEY = 'pathIntegrador';

export interface PostLoginRedirectState {
  /** `false` enquanto os dados do morador (config/pessoa) ainda carregam. */
  ready: boolean;
  /** Destino resolvido; `null` quando não há redirect (sem usuário). */
  destination: string | null;
}

function readLoggedUser(): LoggedUser | null {
  try {
    return decodeUser(sessionStorage.getItem(USER_INSTANCE_KEY));
  } catch {
    return null;
  }
}

function readPathIntegrador(): string | null {
  try {
    return localStorage.getItem(PATH_INTEGRADOR_KEY);
  } catch {
    return null;
  }
}

/**
 * Orquestra a decisão de redirect pós-login: lê usuário e tenant do storage,
 * busca config/pessoa apenas quando o morador com conta precisa (para decidir
 * wizard vs. home) e delega a rota a `resolvePostLoginRedirect`.
 */
export function usePostLoginRedirect(): PostLoginRedirectState {
  const user = useMemo(readLoggedUser, []);
  const tenantPresente = useMemo(() => getTenantStorage() != null, []);

  const contasCount = user?.contas?.length ?? 0;
  // Para moradores, contas.length > 0 garante que o tenant não veio de outro
  // perfil. Para integrador/distribuidor, contas é sempre vazio; o tenant no
  // storage implica seleção ativa desta sessão (logout limpa o sessionStorage
  // por inteiro, então não há tenant remanescente de sessão anterior).
  const accountSelected =
    contasCount > 0 ? tenantPresente : tenantPresente && isIntegratorOrDistributor(user);

  const precisaDadosTenant =
    !isIntegrator(user) && contasCount > 0 && (contasCount === 1 || accountSelected);

  const { person, isLoading: personLoading } = useLoggedPerson(precisaDadosTenant);
  const { config, isLoading: configLoading } = useConfiguracoes(precisaDadosTenant);

  if (precisaDadosTenant && (personLoading || configLoading)) {
    return { ready: false, destination: null };
  }

  const ctx: PostLoginContext = {
    accountSelected,
    pathIntegrador: readPathIntegrador(),
    primeiroAcesso: person?.primeiroAcesso ?? null,
    editarMeusDados: config?.editarMeusDados ?? false,
  };

  return { ready: true, destination: resolvePostLoginRedirect(user, ctx) };
}
